import React, { ReactElement } from "react";
import { FlattenSimpleInterpolation } from "styled-components";

import OffCanvasTray from "../../organisms/OffCanvasTray";
import closeIcon from "../../../assets/images/icons/cross.svg";
import { confettiColors } from "../../../styles/variables";
import { useDeviceSize } from "../../../helpers/hooks";
import {
  StyledRandomRewardsPopupContainer,
  TopBar,
  CloseButton,
  StyledTitle,
  DescriptionAndImageContainer,
  DescriptionContainer,
  ImageContainer,
  DescriptionText,
  StyledConfetti,
  ConfettiAndImageWrapper,
  StyledConfettiWrapper,
  VerticalAnimatingContainer,
  FadingAnimatingContainer,
} from "./style";

interface PointsEarnedModalProps {
  onCancel: Function;
  title: string;
  description: string | ReactElement;
  backgroundColor: string;
  imageComponent: ReactElement;
  textAlign: "left" | "center";
  buttons: Array<ReactElement>;
  showConfetti?: boolean;
  titleStyle?: FlattenSimpleInterpolation;
}

const PointsEarnedModal = ({
  onCancel,
  backgroundColor,
  textAlign,
  imageComponent,
  buttons,
  title,
  description,
  showConfetti = true,
  titleStyle,
}: PointsEarnedModalProps) => {
  const { isExtraSmall } = useDeviceSize();

  /**
   * Renders the title of modal.
   * */
  const renderTitle = (): ReactElement => (
    <StyledTitle textAlign={textAlign} customStyle={titleStyle}>
      {" "}
      {title}{" "}
    </StyledTitle>
  );

  /**
   * Renders the button(s) passed down from the parent.
   * */
  const renderButtons = (): ReactElement => <>{buttons && buttons.map((button) => button)}</>;

  /**
   * Renders the description of modal,
   * and cta buttons to close or navigate to other pages.
   * */
  const renderDescription = (): ReactElement => (
    <>
      <DescriptionText textAlign={textAlign}>{description}</DescriptionText>

      {renderButtons()}
    </>
  );

  /**
   * Renders the confetti.
   * */
  const renderConfetti = (): ReactElement => (
    <StyledConfettiWrapper>
      <StyledConfetti
        colors={confettiColors}
        recycle={false}
        numberOfPieces={350}
        tweenDuration={7000}
        width={1000}
        height={1000}
      />
    </StyledConfettiWrapper>
  );

  /**
   * Renders the gif image and confetti (if enabled).
   * */
  const renderImageAndConfetti = (): ReactElement => (
    <ConfettiAndImageWrapper>
      {imageComponent}
      {showConfetti && renderConfetti()}
    </ConfettiAndImageWrapper>
  );

  /**
   * Renders the description and gif image and confetti in a single row.
   * */
  const renderDescriptionAndImage = (): ReactElement => (
    <DescriptionAndImageContainer>
      <DescriptionContainer>{renderDescription()}</DescriptionContainer>
      <ImageContainer>{renderImageAndConfetti()}</ImageContainer>
    </DescriptionAndImageContainer>
  );

  /**
   * Renders the content of the modal,
   * by handling the various layout for small and large screens.
   * */
  const renderContent = (): ReactElement => {
    if (isExtraSmall) {
      return (
        <>
          {renderImageAndConfetti()}
          {renderTitle()}
          {renderDescription()}
        </>
      );
    }

    return (
      <>
        {renderTitle()}
        {renderDescriptionAndImage()}
      </>
    );
  };

  return (
    <OffCanvasTray
      setOverflowHidden={false}
      isOffcanvasOpened
      outSideClickHandler={() => onCancel()}
      isScrollLockEnabled
      shouldOffCanvasTrayMaskAnimate
    >
      <StyledRandomRewardsPopupContainer>
        <VerticalAnimatingContainer>
          <FadingAnimatingContainer backgroundColor={backgroundColor}>
            <TopBar>
              <CloseButton onClick={() => onCancel()} src={closeIcon} alt="Close" />
            </TopBar>
            {renderContent()}
          </FadingAnimatingContainer>
        </VerticalAnimatingContainer>
      </StyledRandomRewardsPopupContainer>
    </OffCanvasTray>
  );
};

export default PointsEarnedModal;
